import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const brandStory = [
  "91影视诞生于一群影视爱好者的共同想法：让查找影视信息这件事变得简单、清晰、不被打扰。我们不追求繁复的功能堆砌，而是希望每一位来访者都能快速找到想看的内容，了解一部作品的来龙去脉。",
  "从电影到电视剧，从动漫到综艺，我们把作品的导演、主演、题材、地区、年份、评分等关键信息整理在一处，并提供多维筛选与关键词搜索，帮助你在海量内容中快速定位。",
  "在资讯频道，我们聚焦行业动态、档期信息、剧集观察与影评推荐，坚持真实、克制的表达，不制造虚假热度，也不夸大宣传。",
];

const guides = [
  { icon: "ri-search-2-line", title: "如何搜索影视作品", desc: "在顶部搜索框输入片名、演员或导演，即可跳转到搜索结果页查看匹配内容。" },
  { icon: "ri-filter-3-line", title: "如何使用筛选功能", desc: "在分类页可按类型、地区、年份组合筛选，并按更新时间或热门程度排序。" },
  { icon: "ri-share-forward-line", title: "如何分享影片", desc: "进入影片详情页，点击“分享影片”即可生成并复制分享链接。" },
  { icon: "ri-information-line", title: "关于播放功能", desc: "本站目前仅提供影视信息展示与资讯浏览，播放与下载功能尚未开放。" },
];

const socials = [
  { icon: "ri-weibo-fill", label: "官方微博", value: "@91影视" },
  { icon: "ri-wechat-fill", label: "官方公众号", value: "91影视" },
  { icon: "ri-bilibili-fill", label: "哔哩哔哩", value: "91影视官方" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 91影视",
    description: "了解91影视的品牌故事、联系方式与使用说明。91影视为您提供影视作品展示与影视资讯浏览服务。",
    keywords: "91影视,关于我们,影视网站介绍,联系方式",
  });

  const [copied, setCopied] = useState("");

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">关于我们</span>
      </nav>

      <header className="mt-4 max-w-3xl">
        <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">关于 91影视</h1>
        <p className="mt-2 text-sm text-foreground-500">
          一个专注影视内容与资讯的门户平台，为影视爱好者提供清晰、便捷的浏览体验。
        </p>
      </header>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
            品牌故事
          </h2>
          <div className="mt-4 space-y-4">
            {brandStory.map((paragraph) => (
              <p key={paragraph.slice(0, 10)} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-primary-950 p-6 md:p-8">
          <h2 className="text-lg font-bold text-background-50">联系方式</h2>
          <ul className="mt-4 space-y-3">
            <li>
              <button
                type="button"
                onClick={() => handleCopy("support@91yingshi.com")}
                className="flex w-full items-center gap-3 rounded-md bg-primary-900 p-3 text-left transition-colors hover:bg-primary-800"
              >
                <i className="ri-mail-line text-lg text-secondary-400"></i>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-primary-200">用户支持邮箱</span>
                  <span className="block truncate text-sm font-medium text-background-50">
                    support@91yingshi.com
                  </span>
                </span>
                <i className={copied === "support@91yingshi.com" ? "ri-check-line text-secondary-400" : "ri-file-copy-line text-primary-300"}></i>
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => handleCopy("bd@91yingshi.com")}
                className="flex w-full items-center gap-3 rounded-md bg-primary-900 p-3 text-left transition-colors hover:bg-primary-800"
              >
                <i className="ri-hand-coin-line text-lg text-secondary-400"></i>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-primary-200">商务合作邮箱</span>
                  <span className="block truncate text-sm font-medium text-background-50">bd@91yingshi.com</span>
                </span>
                <i className={copied === "bd@91yingshi.com" ? "ri-check-line text-secondary-400" : "ri-file-copy-line text-primary-300"}></i>
              </button>
            </li>
          </ul>
          {copied && <p className="mt-3 text-xs text-secondary-300">邮箱已复制到剪贴板</p>}

          <div className="mt-6 border-t border-primary-800 pt-5">
            <p className="text-xs text-primary-200">关注我们</p>
            <ul className="mt-3 space-y-2.5">
              {socials.map((item) => (
                <li key={item.label} className="flex items-center gap-3 text-sm text-primary-100">
                  <i className={`${item.icon} text-lg text-secondary-400`}></i>
                  <span className="text-primary-300">{item.label}</span>
                  <span className="ml-auto">{item.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
          使用说明
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {guides.map((guide) => (
            <div key={guide.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${guide.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">{guide.title}</h3>
                <p className="mt-1 text-sm text-foreground-500">{guide.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 p-5 text-center">
        <p className="text-sm text-foreground-500">
          本站影视播放功能尚未开放，所有内容均为演示数据。如需了解更多，欢迎通过上方邮箱与我们联系。
        </p>
        <Link
          to="/category/movie"
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-compass-3-line"></i>
          开始浏览影视
        </Link>
      </div>
    </div>
  );
}